export function deepClone<TData = unknown>(
  data?: TData | null
): TData | null | undefined {
  if (!data) {
    return data;
  }

  return JSON.parse(JSON.stringify(data));
}

export function shallowClone<T>(data?: T | null): T {
  if (Array.isArray(data)) {
    return [...data] as T;
  } else if (data !== null && typeof data === 'object') {
    return { ...data } as T;
  } else {
    return data;
  }
}

export function deepCompare<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Adjusts a given date to remove the timezone offset, effectively converting it to a local date.
 * If no date is provided, it defaults to the current date.
 *
 * @param data - The input date to adjust. Defaults to the current date.
 * @returns A new Date object adjusted to the local time zone.
 */
export function offsetAdjustedLocalDate(data?: Date): Date {
  data = data || new Date();
  return new Date(data.getTime() - data.getTimezoneOffset() * 60000);
}

/**
 * Capitalizes the first character of a string and lowercases the rest.
 *
 * Example:
 *   capitalizeFirst('hELLO WORLD') // 'Hello world'
 *
 * @param str - The input string to transform.
 * @returns The string with only the first character capitalized.
 */
export function capitalizeFirst(str: string): string {
  return !str ? '' : str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Safely parses a JSON string. Returns the parsed value, or the original value if parsing fails.
 * Returns null for empty or whitespace-only strings.
 *
 * @param value - The JSON string to parse.
 * @returns The parsed object, null if input is empty, or the original value if parsing fails.
 */
export function parseJson(value: string): unknown {
  if (!value?.trim()) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}
